// Lightweight ZIP/city -> state resolution for the "Check Your Area" tool.
// This is an APPROXIMATE, educational mapping by 3-digit ZIP prefix; it resolves to
// the state level only. The prefix ranges are verified against the USPS 3-digit
// prefix table (see zip-prefixes.test.ts for the locked behavior).
import { STATE_ABBR_TO_NAME } from "./states";

// [prefixStart, prefixEnd, stateAbbr] using the first 3 ZIP digits as an integer.
const ZIP_RANGES: Array<[number, number, string]> = [
  [5, 5, "NY"],
  [10, 27, "MA"],
  [28, 29, "RI"],
  [30, 38, "NH"],
  [39, 49, "ME"],
  [50, 59, "VT"],
  [60, 69, "CT"],
  [70, 89, "NJ"],
  [100, 149, "NY"],
  [150, 196, "PA"],
  [197, 199, "DE"],
  [200, 205, "DC"],
  [206, 219, "MD"],
  [220, 246, "VA"],
  [247, 268, "WV"],
  [270, 289, "NC"],
  [290, 299, "SC"],
  [300, 319, "GA"],
  [320, 349, "FL"],
  [350, 369, "AL"],
  [370, 385, "TN"],
  [386, 397, "MS"],
  [398, 399, "GA"],
  [400, 427, "KY"],
  [430, 459, "OH"],
  [460, 479, "IN"],
  [480, 499, "MI"],
  [500, 528, "IA"],
  [530, 549, "WI"],
  [550, 567, "MN"],
  [570, 577, "SD"],
  [580, 588, "ND"],
  [590, 599, "MT"],
  [600, 629, "IL"],
  [630, 658, "MO"],
  [660, 679, "KS"],
  [680, 693, "NE"],
  [700, 714, "LA"],
  [716, 729, "AR"],
  [730, 749, "OK"],
  [750, 799, "TX"],
  [800, 816, "CO"],
  [820, 831, "WY"],
  [832, 838, "ID"],
  [840, 847, "UT"],
  [850, 865, "AZ"],
  [870, 884, "NM"],
  [885, 885, "TX"],
  [889, 898, "NV"],
  [900, 961, "CA"],
  [967, 968, "HI"],
  [970, 979, "OR"],
  [980, 994, "WA"],
  [995, 999, "AK"],
];

/**
 * What a US ZIP code actually looks like: three to five digits, optionally with the
 * +4 add-on, and nothing else.
 *
 * The old check was `/^\d/` on the raw query followed by stripping every non-digit,
 * which meant any string starting with a digit resolved to a state with full
 * confidence: "1600 Pennsylvania Ave" became prefix 160 and answered Pennsylvania,
 * "10 Downing St" answered Massachusetts. A wrong answer delivered confidently is
 * worse here than no answer, because the whole promise is a trustworthy local one.
 */
const ZIP_SHAPE = /^(\d{3,5})(?:[-\s]?\d{4})?$/;

export function looksLikeZip(query: string): boolean {
  return ZIP_SHAPE.test(query.trim());
}

/**
 * ZIP prefixes that are real US postal codes but which this site publishes nothing
 * for. Telling someone in San Juan to "try a 5-digit ZIP code" when they just did is
 * a dead end that blames them for our gap.
 */
const UNCOVERED: Array<[number, number, string]> = [
  [6, 9, "Puerto Rico and the US Virgin Islands"],
  [90, 98, "military mail (Europe, Africa and the Middle East)"],
  [340, 340, "military mail (the Americas)"],
  [962, 966, "military mail (the Pacific)"],
  [969, 969, "Guam, the Northern Mariana Islands and the western Pacific"],
];

function prefixOf(zip: string): number | null {
  const m = ZIP_SHAPE.exec(zip.trim());
  if (!m) return null;
  const prefix = parseInt(m[1].slice(0, 3).padEnd(3, "0"), 10);
  return Number.isNaN(prefix) ? null : prefix;
}

/**
 * The place a ZIP belongs to, when we publish nothing for it. Callers show this
 * instead of the generic "we couldn't match that" so the message is about our
 * coverage rather than the user's typing.
 */
export function uncoveredZipPlace(zip: string): string | null {
  const prefix = prefixOf(zip);
  if (prefix === null) return null;
  for (const [start, end, place] of UNCOVERED) {
    if (prefix >= start && prefix <= end) return place;
  }
  return null;
}

export function zipToStateAbbr(zip: string): string | null {
  const prefix = prefixOf(zip);
  if (prefix === null) return null;
  if (uncoveredZipPlace(zip)) return null;
  for (const [start, end, abbr] of ZIP_RANGES) {
    if (prefix >= start && prefix <= end) return abbr;
  }
  return null;
}

// A small seed list of well-known cities -> state. Falls back gracefully when unknown.
const CITY_TO_ABBR: Record<string, string> = {
  austin: "TX",
  houston: "TX",
  dallas: "TX",
  "san antonio": "TX",
  "fort worth": "TX",
  atlanta: "GA",
  savannah: "GA",
  augusta: "GA",
  charlotte: "NC",
  raleigh: "NC",
  asheville: "NC",
  durham: "NC",
  nashville: "TN",
  memphis: "TN",
  knoxville: "TN",
  chattanooga: "TN",
  richmond: "VA",
  "virginia beach": "VA",
  roanoke: "VA",
  columbia: "SC",
  charleston: "SC",
  greenville: "SC",
  miami: "FL",
  orlando: "FL",
  tampa: "FL",
  jacksonville: "FL",
  tallahassee: "FL",
  "kansas city": "MO",
  "st louis": "MO",
  "saint louis": "MO",
  springfield: "MO",
  "little rock": "AR",
  fayetteville: "AR",
  louisville: "KY",
  lexington: "KY",
  birmingham: "AL",
  montgomery: "AL",
  huntsville: "AL",
  mobile: "AL",
  jackson: "MS",
  gulfport: "MS",
  "new york": "NY",
  brooklyn: "NY",
  "long island": "NY",
  albany: "NY",
  newark: "NJ",
  trenton: "NJ",
  "jersey city": "NJ",
  philadelphia: "PA",
  pittsburgh: "PA",
  allentown: "PA",
};

export function cityToStateAbbr(city: string): string | null {
  const key = city.trim().toLowerCase();
  return CITY_TO_ABBR[key] ?? null;
}

export interface ResolvedLocation {
  abbr: string;
  stateName: string;
  /** how we resolved it — drives the "state-level only" disclaimer copy */
  method: "zip" | "city" | "state";
  query: string;
  /** For a city match, the place as the reader should see it ("Austin"). */
  place?: string;
}

/** "fort worth" -> "Fort Worth", for echoing a typed place back. */
function titleCase(s: string): string {
  return s
    .trim()
    .replace(/\s+/g, " ")
    .replace(
      /\b([a-z])([a-z']*)/gi,
      (_, a: string, b: string) => a.toUpperCase() + b.toLowerCase(),
    );
}

const NAME_TO_ABBR: Record<string, string> = Object.fromEntries(
  Object.entries(STATE_ABBR_TO_NAME).map(([abbr, name]) => [name.toLowerCase(), abbr]),
);

/**
 * "Austin, TX", "Austin TX", "Portland, Maine", "Fulton County, GA": the reader has told
 * us the state, so trust it, whatever the place before it is. Without a comma, a bare
 * two-letter tail only counts in capitals ("Austin TX"), because "in", "me", "or" and
 * "ok" are words before they are states.
 */
export function stateQualified(q: string): { abbr: string; place: string } | null {
  const comma = q.lastIndexOf(",");
  if (comma > 0) {
    const place = q.slice(0, comma).trim();
    const tail = q.slice(comma + 1).trim();
    const abbr =
      NAME_TO_ABBR[tail.toLowerCase()] ??
      (STATE_ABBR_TO_NAME[tail.toUpperCase()] ? tail.toUpperCase() : null);
    if (abbr && /[a-z]/i.test(place)) return { abbr, place: place.split(",")[0].trim() };
  }
  const words = q.split(/\s+/);
  for (const n of [3, 2, 1]) {
    if (words.length <= n) continue;
    const tail = words.slice(-n).join(" ");
    const place = words.slice(0, -n).join(" ");
    const byName = NAME_TO_ABBR[tail.toLowerCase()];
    if (byName) return { abbr: byName, place };
    if (n === 1 && /^[A-Z]{2}$/.test(tail) && STATE_ABBR_TO_NAME[tail])
      return { abbr: tail, place };
  }
  return null;
}

/** Resolve a free-text query (ZIP, city, or state) to a state. */
export function resolveLocation(query: string): ResolvedLocation | null {
  const q = query.trim();
  if (!q) return null;

  // ZIP — only when it is shaped like one.
  if (looksLikeZip(q)) {
    const abbr = zipToStateAbbr(q);
    if (abbr) return { abbr, stateName: STATE_ABBR_TO_NAME[abbr], method: "zip", query: q };
    return null;
  }

  // exact state name
  const byName = Object.entries(STATE_ABBR_TO_NAME).find(
    ([, name]) => name.toLowerCase() === q.toLowerCase(),
  );
  if (byName) {
    return { abbr: byName[0], stateName: byName[1], method: "state", query: q };
  }

  // state abbreviation
  const upper = q.toUpperCase();
  if (STATE_ABBR_TO_NAME[upper]) {
    return { abbr: upper, stateName: STATE_ABBR_TO_NAME[upper], method: "state", query: q };
  }

  // city
  const cityAbbr = cityToStateAbbr(q);
  if (cityAbbr) {
    return {
      abbr: cityAbbr,
      stateName: STATE_ABBR_TO_NAME[cityAbbr],
      method: "city",
      query: q,
      place: titleCase(q),
    };
  }

  // "Austin, TX" and friends: a place, then the state it is in.
  const qualified = stateQualified(q);
  if (qualified) {
    return {
      abbr: qualified.abbr,
      stateName: STATE_ABBR_TO_NAME[qualified.abbr],
      method: "city",
      query: q,
      place: titleCase(qualified.place),
    };
  }

  // A pasted address that ends in a ZIP ("1 Main St, Atlanta, GA 30301"): use the ZIP.
  // Only at the very end, so a house number is never read as one.
  const trailingZip = q.match(/[a-z].*?\b(\d{5})(?:-\d{4})?\s*$/i);
  if (trailingZip) {
    const abbr = zipToStateAbbr(trailingZip[1]);
    if (abbr)
      return { abbr, stateName: STATE_ABBR_TO_NAME[abbr], method: "zip", query: trailingZip[1] };
  }

  return null;
}
